import { Button, TextField, Typography } from "@mui/material";
import { Controller, ControllerRenderProps, useForm } from "react-hook-form";
import { textFieldStyles, submitButtonStyles, backButtonStyles } from "./theme";
import { PhoneNumberFormat } from "@src/shared/ui/PhoneNumberFormat/PhoneNumberFormat";
import { reachGoal } from "@src/shared/lib/analytics/yandex/reachGoal";

import styles from "./styles.module.scss";
import { YM_GOALS } from "@src/shared/lib/analytics/yandex/constants";

interface RegistrationStep3Props {
  name: string;
  lastName: string;
  phone: string;
}
interface Props {
  setStep: (step: number) => void;
  setRegistrationData: (data: any) => void;
}

const createNameRules = (fieldLabel: string) => ({
  validate: {
    requiredTrimmed: (value: string) => {
      if (!value.trim()) {
        return "Поле обязательно для заполнения";
      }
      return true;
    },
    validFormat: (value: string) => {
      const trimmedValue = value.trim();

      if (!/^[a-zA-ZА-Яа-яЁё-\s]{2,30}$/.test(trimmedValue)) {
        return `Поле "${fieldLabel}" должно содержать от 2 до 30 символов, только буквы, дефис и пробелы`;
      }

      return true;
    },
  },
});

export const RegistrationStep3 = ({ setStep, setRegistrationData }: Props) => {
  const onSubmit = (data: RegistrationStep3Props) => {
    setRegistrationData((prev: any) => ({
      ...prev,
      lastName: data.lastName.trim(),
      firstName: data.name.trim(),
      phone: data.phone,
    }));
    reachGoal(YM_GOALS.REGISTRATION_STEP(3));
    setStep(4);
  };

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<RegistrationStep3Props>({
    defaultValues: {
      name: "",
      lastName: "",
      phone: "",
    },
    mode: "onChange",
  });

  return (
    <>
      <div className={styles.formTitleContainer}>
        <Typography className={styles.formTitle}>Регистрация</Typography>
        <Typography className={styles.formTitle2}>Шаг 3</Typography>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
        <Controller
          name="lastName"
          control={control}
          rules={createNameRules("Фамилия")}
          render={({
            field,
          }: {
            field: ControllerRenderProps<RegistrationStep3Props, "lastName">;
          }) => (
            <TextField
              {...field}
              label="Фамилия"
              variant="outlined"
              type="text"
              fullWidth
              error={!!errors.lastName}
              helperText={errors.lastName?.message}
              className={styles.input}
              InputProps={{
                style: {
                  borderRadius: 24,
                  color: "#fff",
                },
              }}
              InputLabelProps={{
                style: { color: "#ffffff83" },
              }}
              sx={textFieldStyles}
            />
          )}
        />
        <Controller
          name="name"
          control={control}
          rules={createNameRules("Имя")}
          render={({
            field,
          }: {
            field: ControllerRenderProps<RegistrationStep3Props, "name">;
          }) => (
            <TextField
              {...field}
              label="Имя"
              variant="outlined"
              type="text"
              fullWidth
              error={!!errors.name}
              helperText={errors.name?.message}
              className={styles.input}
              InputProps={{
                style: {
                  borderRadius: 24,
                  color: "#fff",
                },
              }}
              InputLabelProps={{
                style: { color: "#ffffff83" },
              }}
              sx={textFieldStyles}
            />
          )}
        />
        <Controller
          name="phone"
          control={control}
          rules={{
            validate: (value) => {
              if (!value) return true;
              const phoneNumber = value.replace(/\D/g, "");
              if (phoneNumber.length !== 10) {
                return "Номер телефона должен содержать 10 цифр";
              }
              return true;
            },
          }}
          render={({ field }) => (
            <TextField
              {...field}
              label=""
              variant="outlined"
              fullWidth
              error={!!errors.phone}
              helperText={errors.phone?.message}
              className={styles.input}
              InputProps={{
                inputComponent: PhoneNumberFormat as any,
                style: {
                  borderRadius: 24,
                  color: "#fff",
                },
              }}
              InputLabelProps={{
                style: { color: "#ffffff83" },
              }}
              sx={textFieldStyles}
            />
          )}
        />
        <Button
          disabled={!isValid}
          type="submit"
          variant="contained"
          className={styles.button}
          sx={submitButtonStyles}
        >
          Продолжить
        </Button>
      </form>
      <Button variant="text" onClick={() => setStep(2)} sx={backButtonStyles}>
        Вернуться
      </Button>
    </>
  );
};
